import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Maximize2, Save } from 'lucide-react';
import api from '../../utils/api';

const PRIMARY_BTN = 'bg-blue-600 hover:bg-blue-700 text-white';

export default function SlipFormatSelector({
  category,
  templates,
  activeTemplateId,
  schoolId,
  saving,
  onSave,
  onOpenModal,
}) {
  const [draftId, setDraftId] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');

  const sortedTemplates = useMemo(
    () => [...(templates || [])].sort((a, b) => (a.display_order || 0) - (b.display_order || 0)),
    [templates],
  );

  useEffect(() => {
    setDraftId(activeTemplateId || sortedTemplates[0]?.id || '');
  }, [activeTemplateId, sortedTemplates, schoolId, category]);

  useEffect(() => {
    if (!draftId || !schoolId) {
      setPreviewHtml('');
      return undefined;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError('');
    api
      .get('/slips/templates/preview', {
        params: { category, template_id: draftId, school_id: schoolId },
      })
      .then((res) => {
        if (cancelled) return;
        const payload = res.data?.data;
        const raw = typeof payload === 'string' ? payload : payload?.html;
        setPreviewHtml(raw || '');
        if (!raw) setPreviewError('Preview HTML was empty');
      })
      .catch((e) => {
        if (cancelled) return;
        setPreviewError(
          e?.response?.data?.message
          || (typeof e?.response?.data === 'string' ? e.response.data : null)
          || 'Failed to load preview',
        );
        setPreviewHtml('');
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => { cancelled = true; };
  }, [draftId, category, schoolId]);

  const selectedTemplate = sortedTemplates.find((t) => String(t.id) === String(draftId));
  const isDirty = draftId && String(draftId) !== String(activeTemplateId);

  return (
    <div className="space-y-4 rounded-xl border border-border p-4 bg-muted/10">
      <div className="grid gap-4 md:grid-cols-[1fr_auto] md:items-end">
        <div className="space-y-2">
          <Label htmlFor={`slip-format-${category}`}>Slip format</Label>
          <Select value={draftId} onValueChange={setDraftId} disabled={!sortedTemplates.length}>
            <SelectTrigger id={`slip-format-${category}`}>
              <SelectValue placeholder="Select format" />
            </SelectTrigger>
            <SelectContent>
              {sortedTemplates.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name || t.id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {activeTemplateId && !isDirty && (
            <p className="text-xs text-muted-foreground">
              Saved format: {sortedTemplates.find((t) => String(t.id) === String(activeTemplateId))?.name || activeTemplateId}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            className={PRIMARY_BTN}
            disabled={!isDirty || saving}
            onClick={() => onSave?.(category, draftId)}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save format
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={!draftId || previewLoading}
            onClick={() => selectedTemplate && onOpenModal?.(selectedTemplate)}
          >
            <Maximize2 className="h-4 w-4 mr-2" />
            Full preview
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-border overflow-hidden bg-white">
        <div className="px-3 py-2 border-b border-border bg-muted/30 text-xs text-muted-foreground">
          Live preview
          {selectedTemplate?.name ? ` — ${selectedTemplate.name}` : ''}
        </div>
        <div className="h-[420px] bg-muted/10">
          {previewLoading ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Loading preview…
            </div>
          ) : previewError ? (
            <div className="h-full flex items-center justify-center text-sm text-red-500 px-4 text-center">
              {previewError}
            </div>
          ) : (
            <iframe
              title={`slip-preview-${category}`}
              className="w-full h-full bg-white"
              sandbox="allow-same-origin"
              srcDoc={previewHtml}
            />
          )}
        </div>
      </div>
    </div>
  );
}
